import styled, { css } from 'styled-components';
import { getBodyStyle, getColor, type Theme } from 'styles/theme';

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const InputsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  & > * {
    width: calc((100% - 16px) / 2);
  }
`;

export const RangeWrapper = styled.div`
  padding: 4px 0 28px 0; // 28px on the bottom to compensate for the abosolute positioned elements
  width: 100%;
  position: relative;
  margin-top: 4px;
`;

export const RangeInputsWrapper = styled.div`
  display: flex;
`;

const getBackgroundColor = (variant: string) =>
  variant === 'brand' ? getColor('shadeBrandPrimary900') : getColor('surfaceBlack');

export const RangeInput = styled.input<{ theme: Theme; $variant: string }>`
  -webkit-appearance: none; 
  appearance: none;
  pointer-events: none;
  height: 0;
  position: absolute;
  width: 100%;
  outline: none;

  &[id^=from] {
    z-index: 3;
  }

  &[id^=to] {
    z-index: 4;
  }

  &[id^=from]::-webkit-slider-thumb,
  &[id^=to]::-webkit-slider-thumb {
    -webkit-appearance: none;
    pointer-events: all;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    background-color: ${getColor('surfaceWhite')};
    border-radius: 50%;
    border: 3px solid ${({ $variant }) => getBackgroundColor($variant)};
    cursor: pointer;
    position: relative;
  }

  &[id^=from]::-moz-range-thumb,
  &[id^=to]::-moz-range-thumb {
    pointer-events: all;
    width: 16px;
    height: 16px;
    margin-top: 2px;
    background-color: ${getColor('surfaceWhite')};
    border-radius: 50%;
    border: 3px solid ${({ $variant }) => getBackgroundColor($variant)};
    cursor: pointer;
    position: relative;
  }
`;

export const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const SliderTrack = styled.div<{ theme: Theme }>`
  position: absolute;
  height: 3px;
  border-radius: 22.5px;
  width: 100%;
  background-color: ${getColor('surfaceSecondary')};
  z-index: 1;
`;

export const SliderRange = styled.div<{ theme: Theme; $variant: string; $minPercent: number; $maxPercent: number }>`
  position: absolute;
  background-color: ${({ $variant }) => getBackgroundColor($variant)};
  width: 100%;
  height: 3px;
  z-index: 2;

  ${({ $minPercent, $maxPercent }) => ({
    left: $minPercent === 0 ? '0' : `${$minPercent}%`,
    width: `${$maxPercent - $minPercent}%`,
  })}
`;

export const SliderThumb = styled.div<{
  theme: Theme;
  $variant: string;
  $minPercent: number;
  $maxPercent: number;
  $width: number;
}>`
  position: absolute;
  ${getBodyStyle('graphikCond', { default: 'xsmall' })}
  background-color: ${({ $variant }) => getBackgroundColor($variant)};
  color: ${getColor('textWhite')};
  font-weight: 500;
  line-height: 12px;
  padding: 4px;
  margin-top: 12px;

  &::after {
    content: "";
    position: absolute;
    bottom: 95%;
    border-width: 5px;
    border-style: solid;
    border-color: transparent transparent ${({ $variant }) => getBackgroundColor($variant)} transparent;
  }

  &.left {
    ${({ $minPercent, $width }) =>
      $minPercent > 5
        ? css`
        left: calc(${$minPercent}% - ${$width}px);
        &::after {
          right: 0;
        }
      `
        : css`
        left: ${$minPercent}%;
        &::after {
          left: 0;
        } 
    `}
  }

  &.right {
    ${({ $maxPercent, $width }) =>
      $maxPercent < 95
        ? css`
        right: calc(${100 - $maxPercent}% - ${$width}px);
        &::after {
          left: 0;
        }
    `
        : css`
        right: calc(${100 - $maxPercent}%);
        &::after {
          right: 0;
        } 
    `}
  }
`;
